import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mail, CheckCircle2, XCircle, Heart } from "lucide-react";
import api from "../lib/api";

const Unsubscribe = () => {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [state, setState] = useState({ loading: true, email: "", already: false, error: "" });
    const [reason, setReason] = useState("");
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setState({ loading: false, error: "Missing unsubscribe token." });
            return;
        }
        api.get("/unsubscribe/verify", { params: { token } })
            .then(({ data }) => setState({ loading: false, email: data.email, already: data.already_unsubscribed, error: "" }))
            .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Invalid link" }));
    }, [token]);

    const confirm = async () => {
        setSubmitting(true);
        try {
            await api.post("/unsubscribe", { token, reason });
            setDone(true);
        } catch (e) {
            setState((s) => ({ ...s, error: e?.response?.data?.detail || "Failed to unsubscribe" }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-20" data-testid="unsubscribe-page">
            <div className="max-w-lg w-full bg-white rounded-3xl border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-10">
                <div className="w-14 h-14 rounded-full bg-[#F2D8D5] flex items-center justify-center mb-6">
                    <Mail className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                </div>
                {state.loading ? (
                    <p className="text-sm text-[#4A5568]">Verifying link…</p>
                ) : state.error ? (
                    <>
                        <h1 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">Link expired</h1>
                        <p className="text-sm text-[#4A5568] flex items-center gap-2 mb-6">
                            <XCircle className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                            {state.error}
                        </p>
                        <Link to="/" className="inline-flex items-center text-sm font-medium text-[#E53935] hover:underline">
                            Back to home
                        </Link>
                    </>
                ) : done || state.already ? (
                    <>
                        <h1 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">You're unsubscribed</h1>
                        <p className="text-sm text-[#4A5568] flex items-center gap-2 mb-6" data-testid="unsubscribe-success">
                            <CheckCircle2 className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                            <span><span className="font-mono">{state.email}</span> won't receive marketing emails from us.</span>
                        </p>
                        <p className="text-xs text-[#4A5568] mb-6 flex items-start gap-2">
                            <Heart className="w-3.5 h-3.5 text-[#E53935] flex-shrink-0 mt-0.5" fill="#E53935" strokeWidth={1.5} />
                            <span>Tax receipts and order confirmations may still be sent — those are required by law.</span>
                        </p>
                        <Link to="/" data-testid="unsubscribe-home-link" className="inline-flex items-center text-sm font-medium text-[#E53935] hover:underline">
                            Back to home
                        </Link>
                    </>
                ) : (
                    <>
                        <h1 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">Unsubscribe?</h1>
                        <p className="text-sm text-[#4A5568] mb-2">
                            Stop sending marketing emails to <span className="font-mono text-[#1C252E]">{state.email}</span>?
                        </p>
                        <p className="text-xs text-[#4A5568] mb-6">
                            You'll still receive transactional messages like tax receipts and order confirmations.
                        </p>
                        <label className="block text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] mb-2">
                            Tell us why (optional)
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            data-testid="unsubscribe-reason-input"
                            placeholder="Too many emails / no longer interested / …"
                            className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                        <div className="flex items-center gap-3">
                            <button
                                onClick={confirm}
                                disabled={submitting}
                                data-testid="unsubscribe-confirm-btn"
                                className="inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50"
                            >
                                {submitting ? "Unsubscribing…" : "Yes, unsubscribe me"}
                            </button>
                            <Link
                                to="/"
                                data-testid="unsubscribe-cancel-link"
                                className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E]"
                            >
                                Keep me on the list
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Unsubscribe;
